import type { Channel, OrganizationState } from '@controlstock/contracts';

type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const toneClasses: Record<StatusTone, string> = {
  success: 'border-mint-100 bg-mint-50 text-mint-600',
  warning: 'border-amber-100 bg-amber-50 text-amber-700',
  danger: 'border-rose-100 bg-rose-50 text-rose-700',
  info: 'border-brand-100 bg-brand-50 text-brand-700',
  neutral: 'border-slate-200 bg-slate-50 text-slate-600',
};

const statusTone: Record<string, StatusTone> = {
  ACTIVE: 'success',
  CONNECTED: 'success',
  SYNCED: 'success',
  SYNC_PENDING: 'info',
  SYNCING: 'info',
  SYNC_RETRYING: 'warning',
  SUCCEEDED: 'success',
  COMPLETED: 'success',
  PLANNED: 'info',
  IN_PROGRESS: 'info',
  CANCELLED: 'danger',
  SETUP: 'info',
  READ_ONLY: 'warning',
  TRIAL: 'warning',
  PENDING: 'warning',
  RUNNING: 'info',
  RETRYING: 'warning',
  DIFFERENCE: 'warning',
  AMBIGUOUS: 'warning',
  UNLINKED: 'warning',
  DISCONNECTED: 'neutral',
  OUT_OF_STOCK: 'neutral',
  ERROR: 'danger',
  DEAD_LETTER: 'danger',
  FAILED: 'danger',
  IN_CUSTODY: 'warning',
  DEPOSITED: 'info',
  CASHED: 'success',
  ISSUED: 'info',
  DELIVERED: 'success',
  REJECTED: 'danger',
  REAUTH_REQUIRED: 'danger',
  DUPLICATE_SKU: 'danger',
  MISSING_SKU: 'danger',
};

const labels: Record<string, string> = {
  ACTIVE: 'Activo',
  SETUP: 'Configuración',
  READ_ONLY: 'Solo lectura',
  TRIAL: 'Prueba interna',
  CONNECTED: 'Conectado',
  DISCONNECTED: 'Desconectado',
  REAUTH_REQUIRED: 'Reconexión',
  SYNCED: 'Sincronizado',
  SYNC_PENDING: 'En espera',
  SYNCING: 'En curso',
  SYNC_RETRYING: 'Reintentando',
  DIFFERENCE: 'Con diferencia',
  ERROR: 'Error',
  UNLINKED: 'Sin vincular',
  OUT_OF_STOCK: 'Sin stock',
  LINKED: 'Vinculado',
  AMBIGUOUS: 'Ambiguo',
  DUPLICATE_SKU: 'SKU duplicado',
  MISSING_SKU: 'Sin SKU',
  PENDING: 'Pendiente',
  RUNNING: 'En curso',
  SUCCEEDED: 'Completado',
  COMPLETED: 'Completada',
  PLANNED: 'Registrada',
  IN_PROGRESS: 'En proceso',
  CANCELLED: 'Anulada',
  RETRYING: 'Reintentando',
  DEAD_LETTER: 'Requiere atención',
  FAILED: 'Fallida',
  IN_CUSTODY: 'En cartera',
  DEPOSITED: 'Depositado',
  CASHED: 'Cobrado',
  ISSUED: 'Emitido',
  DELIVERED: 'Entregado',
  REJECTED: 'Rechazado',
};

function titleize(value: string): string {
  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

export function statusLabel(status: string): string {
  return labels[status] ?? titleize(status);
}

export function StatusBadge({
  status,
  label,
}: Readonly<{ status: string; label?: string }>): React.JSX.Element {
  const tone = statusTone[status] ?? 'neutral';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${toneClasses[tone]}`}
    >
      <span className="size-1.5 rounded-full bg-current opacity-75" aria-hidden="true" />
      {label ?? statusLabel(status)}
    </span>
  );
}

export function OrganizationStateBadge({
  state,
}: Readonly<{ state: OrganizationState }>): React.JSX.Element {
  return <StatusBadge status={state} />;
}

const channelMeta: Record<Channel, { label: string; short: string; className: string }> = {
  TIENDANUBE: {
    label: 'Tiendanube',
    short: 'TN',
    className: 'bg-sky-50 text-sky-700 ring-sky-100',
  },
  MERCADOLIBRE: {
    label: 'Mercado Libre',
    short: 'ML',
    className: 'bg-yellow-50 text-yellow-800 ring-yellow-100',
  },
};

export function ChannelBadge({
  channel,
  showLabel = true,
  accountLabel,
}: Readonly<{
  channel: Channel;
  showLabel?: boolean;
  accountLabel?: string | undefined;
}>): React.JSX.Element {
  const metadata = channelMeta[channel];
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
      <span
        className={`grid size-6 place-items-center rounded-lg text-[9px] font-bold ring-1 ${metadata.className}`}
        aria-hidden="true"
      >
        {accountLabel ?? metadata.short}
      </span>
      {showLabel && (accountLabel ?? metadata.label)}
    </span>
  );
}

export function movementLabel(type: string): string {
  const movementLabels: Record<string, string> = {
    INITIAL_IMPORT: 'Importación inicial',
    SALE: 'Venta',
    CANCELLATION: 'Cancelación',
    RETURN: 'Devolución',
    MANUAL_ADD: 'Ajuste manual · suma',
    MANUAL_SUBTRACT: 'Ajuste manual · resta',
    MANUAL_SET: 'Ajuste manual · establecer',
    CORRECTION: 'Corrección',
    RECONCILIATION: 'Reconciliación',
  };
  return movementLabels[type] ?? titleize(type);
}
